import {
  createSubscriptionPaymentApiCall,
  getSubscriptionPlansApiCall,
  getSubscriptionStatusApiCall,
} from '@/api/subscription'
import type { SubscriptionPlan, SubscriptionStatus } from '@/types/subscription'
import { computed, defineComponent, onMounted, onUnmounted, ref } from 'vue'

type DialogStep = 'overview' | 'plans' | 'confirm' | 'awaiting'
type PresentationTone = 'neutral' | 'success' | 'warning' | 'danger'

interface StatusPresentation {
  tone: PresentationTone
  icon: string
  badge: string
  title: string
  description: string
  action: string
}

const planNames: Record<string, string> = {
  month: '1 месяц',
  quarter: '3 месяца',
  half_year: '6 месяцев',
  year: '12 месяцев',
  test: 'Тестовый доступ',
}

const monthForms = ['месяц', 'месяца', 'месяцев']

export default defineComponent({
  name: 'SubscriptionControl',
  setup() {
    const isOpen = ref(false)
    const isLoading = ref(false)
    const isPaymentStarting = ref(false)
    const status = ref<SubscriptionStatus | null>(null)
    const plans = ref<SubscriptionPlan[]>([])
    const selectedPlan = ref<SubscriptionPlan | null>(null)
    const dialogStep = ref<DialogStep>('overview')
    const requestError = ref('')
    const paymentUrl = ref<string | null>(null)

    const planName = (code: string) => planNames[code] ?? code
    const planTitle = (plan: SubscriptionPlan) => {
      if (planNames[plan.code])
        return planNames[plan.code]
      if (plan.months === 0)
        return 'Тестовый доступ'
      const index = plan.months % 10 === 1 && plan.months !== 11 ? 0 : plan.months % 10 >= 2 && plan.months % 10 <= 4 && (plan.months < 10 || plan.months > 20) ? 1 : 2
      return `${plan.months} ${monthForms[index]}`
    }
    const formatMoney = (amountKopecks: number, currency = 'RUB') =>
      new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amountKopecks / 100)
    const formatDate = (date: string) =>
      new Intl.DateTimeFormat('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(new Date(date))
    const planMonthlyPrice = (plan: SubscriptionPlan) => {
      if (plan.months < 1)
        return 'Разовая оплата'
      const monthly = Math.round(plan.amountKopecks / plan.months)
      return `${formatMoney(monthly, plan.currency)}/мес`
    }

    const statusPresentation = computed<StatusPresentation>(() => {
      if (!status.value) {
        return {
          tone: 'neutral',
          icon: '…',
          badge: 'Статус уточняется',
          title: 'Проверяем подписку',
          description: 'Обновите данные, чтобы увидеть статус.',
          action: 'Обновить',
        }
      }

      if (status.value.status === 'active') {
        return {
          tone: 'success',
          icon: '✓',
          badge: 'Подписка активна',
          title: 'Всё работает',
          description: 'Ваше приложение опубликовано и доступно пользователям.',
          action: 'Продлить подписку',
        }
      }
      if (status.value.status === 'paid_waiting_activation') {
        return {
          tone: 'warning',
          icon: '◌',
          badge: 'Оплата получена',
          title: 'Ждём публикацию',
          description: 'Подписка оплачена и начнётся после публикации приложения в сторах.',
          action: 'Выбрать другой тариф',
        }
      }
      if (status.value.status === 'expired') {
        const days = status.value.expiredDays ?? 0
        return {
          tone: status.value.mustUnpublish ? 'danger' : 'warning',
          icon: '!',
          badge: `Истекла ${days} ${days === 1 ? 'день' : days < 5 ? 'дня' : 'дней'} назад`,
          title: status.value.mustUnpublish ? 'Приложение пора снять с публикации' : 'Подписку нужно продлить',
          description: status.value.mustUnpublish
            ? 'Срок оплаты истёк более 7 дней назад.'
            : 'Оплатите тариф, чтобы не допустить снятия приложения из сторов.',
          action: 'Оплатить подписку',
        }
      }
      return {
        tone: 'neutral',
        icon: '○',
        badge: 'Подписка не оплачена',
        title: 'Подключите подписку',
        description: 'После оплаты подписка начнёт действовать с момента публикации приложения в сторах.',
        action: 'Выбрать тариф',
      }
    })

    const triggerText = computed(() => {
      if (!status.value)
        return 'Проверить'
      if (status.value.status === 'active' && status.value.paidUntil) {
        return `до ${new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: '2-digit' }).format(new Date(status.value.paidUntil))}`
      }
      if (status.value.status === 'expired')
        return 'Продлить'
      return status.value.status === 'paid_waiting_activation' ? 'Ожидает запуск' : 'Оплатить'
    })

    const refreshData = async () => {
      isLoading.value = true
      requestError.value = ''
      try {
        const [nextStatus, nextPlans] = await Promise.all([
          getSubscriptionStatusApiCall(),
          getSubscriptionPlansApiCall(),
        ])
        status.value = nextStatus
        plans.value = nextPlans
      }
      catch {
        requestError.value = 'Не удалось получить данные подписки. Проверьте соединение и попробуйте ещё раз.'
      }
      finally {
        isLoading.value = false
      }
    }

    const openDialog = async () => {
      isOpen.value = true
      dialogStep.value = 'overview'
      await refreshData()
    }
    const closeDialog = () => {
      isOpen.value = false
      requestError.value = ''
    }
    const showPlans = () => {
      dialogStep.value = 'plans'
    }
    const selectPlan = (plan: SubscriptionPlan) => {
      selectedPlan.value = plan
      dialogStep.value = 'confirm'
      requestError.value = ''
    }
    const startPayment = async () => {
      if (!selectedPlan.value)
        return

      requestError.value = ''
      isPaymentStarting.value = true
      const paymentTab = window.open('', '_blank')
      try {
        const payment = await createSubscriptionPaymentApiCall(selectedPlan.value.code)
        if (!payment.paymentUrl)
          throw new Error('Payment URL was not returned')

        paymentUrl.value = payment.paymentUrl
        if (paymentTab)
          paymentTab.location.href = payment.paymentUrl
        else window.open(payment.paymentUrl, '_blank', 'noopener')
        dialogStep.value = 'awaiting'
      }
      catch {
        paymentTab?.close()
        requestError.value = 'Не удалось создать счёт. Попробуйте ещё раз или обратитесь в поддержку.'
      }
      finally {
        isPaymentStarting.value = false
      }
    }
    const openPaymentAgain = () => {
      if (paymentUrl.value)
        window.open(paymentUrl.value, '_blank', 'noopener')
    }
    const refreshAfterPayment = async () => {
      await refreshData()
      dialogStep.value = 'overview'
    }
    const onKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen.value)
        closeDialog()
    }

    onMounted(() => {
      window.addEventListener('keydown', onKeydown)
      void refreshData()
    })
    onUnmounted(() => window.removeEventListener('keydown', onKeydown))

    return {
      isOpen,
      isLoading,
      isPaymentStarting,
      status,
      plans,
      selectedPlan,
      dialogStep,
      requestError,
      paymentUrl,
      statusPresentation,
      triggerText,
      planName,
      planTitle,
      formatMoney,
      formatDate,
      planMonthlyPrice,
      openDialog,
      closeDialog,
      refreshData,
      showPlans,
      selectPlan,
      startPayment,
      openPaymentAgain,
      refreshAfterPayment,
    }
  },
})
